import React, { memo } from 'react'
import ClearIcon from 'transfix-icons/build/ClearIcon'
import SearchIcon from 'transfix-icons/build/SearchIcon'
import Badge from 'transfix-ui/components/Badge'
import { Button } from 'transfix-ui/components/Button'
import Copy from 'transfix-ui/components/Copy'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { Typography, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { ISavedSearch } from '../types'

export interface ISavedSearchItemProps {
  search: ISavedSearch
  onRemove: (id: string) => void
  onSaveDefault: (newDefault: ISavedSearch) => void
}

const useSavedSearchItemStyles = makeStyles(theme => ({
  clearButton: {
    visibility: 'hidden',
  },
  searchRowContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    position: 'relative',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    paddingRight: '5.5px',
    maxHeight: '35px',
    '&:hover, &:active, &:focus': {
      background: brandColors.coolGray1,
      '& $clearButton': {
        visibility: 'visible',
      },
      '& $setDefaultButton': {
        visibility: 'visible',
      },
    },
  },
  searchItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '& a': {
      color: brandColors.coolGray8,
      '& span': {
        justifyContent: 'flex-start',
      },
    },
  },
  setDefaultButton: {
    visibility: 'hidden',
  },
}))

const MAX_CHARACTER_OVERFLOW = 60

export const SavedSearchItem = memo<ISavedSearchItemProps>(
  ({ onRemove, onSaveDefault, search }) => {
    const classes = useSavedSearchItemStyles()
    const { default: defaultSearch = false, filterName, id, url } = search || {}
    const textOverflow = filterName.length > MAX_CHARACTER_OVERFLOW
    const filterNameDisplay = textOverflow
      ? filterName.slice(0, MAX_CHARACTER_OVERFLOW).concat('...')
      : filterName

    const removeSavedSearch = () => {
      onRemove(id)
    }

    const saveDefaultSearch = () => {
      onSaveDefault(search)
    }

    return (
      <div className={classes.searchRowContainer}>
        <div key={id} className={classes.searchItem}>
          <SearchIcon size='medium' />
          {textOverflow ? (
            <Tooltip
              trigger={
                <Button href={url} variant='text'>
                  <Typography variant='body2'>{filterNameDisplay}</Typography>
                </Button>
              }>
              {filterName}
            </Tooltip>
          ) : (
            <Button href={url} variant='text'>
              <Typography variant='body2'>{filterNameDisplay}</Typography>
            </Button>
          )}
          <Copy alignClass='right' copyText={url} displayText='URL' useIcon />
          {defaultSearch ? (
            <Badge label='Default' />
          ) : (
            <div className={classes.setDefaultButton}>
              <Button color='tertiary' variant='text' onClick={saveDefaultSearch}>
                Set Default
              </Button>
            </div>
          )}
        </div>
        <div className={classes.clearButton}>
          <Button onClick={removeSavedSearch} color='tertiary' variant='text' noPadding>
            <ClearIcon size='large' />
          </Button>
        </div>
      </div>
    )
  }
)
